import {
  MissingMerchantRateMode,
  PricingStrategy,
  SpreadUnit,
  UpsRateType,
} from "@redotech/redo-model/outbound-labels/pricing-calculator";
import * as zod from "zod/v4";

const addressSchema_ = zod.object({
  street1: zod.string(),
  city: zod.string(),
  state: zod.string(),
  postalCode: zod.string(),
  country: zod.string(),
});
export const AddressSchema: zod.ZodType<AddressSchema> = addressSchema_;
export interface AddressSchema extends zod.infer<typeof addressSchema_> {}

const distanceResultSchema_ = zod.object({
  distributionCenter: zod.string(),
  distanceMiles: zod.number(),
  durationMinutes: zod.number(),
});

const distanceEligibilitySchema_ = zod.object({
  eligible: zod.boolean(),
  nearestDC: zod.string(),
  distanceMiles: zod.number(),
  allDistances: zod.array(distanceResultSchema_),
});

const carrierEligibilitySchema_ = zod.object({
  dhl: distanceEligibilitySchema_.nullable(),
  amazon: zod.object({ eligible: zod.boolean() }).passthrough(),
  ups: zod.object({ eligible: zod.boolean() }).passthrough(),
  usps: zod.object({ eligible: zod.boolean() }).passthrough(),
  uniuni: distanceEligibilitySchema_.nullable(),
});

const serviceLevelStatsSchema_ = zod.object({
  Standard: zod.number(),
  Plus: zod.number(),
  Express: zod.number(),
});

const dhlSettingsSchema_ = zod.object({
  needsGL: zod.boolean(),
  weeklyPickups: zod.number(),
});

const uspsRevShareSettingsSchema_ = zod.object({
  underOneLbPct: zod.number(),
  overOneLbPct: zod.number(),
});

const upsRevShareSettingsSchema_ = zod.object({
  activeRateType: zod.nativeEnum(UpsRateType),
  groundSaverPct: zod.number(),
  otherServicesPct: zod.number(),
});

const revShareSettingsSchema_ = zod.object({
  usps: uspsRevShareSettingsSchema_,
  ups: upsRevShareSettingsSchema_,
});

const rateCalculatorSessionConfigSchema_ = zod.object({
  step: zod.number(),
  sessionName: zod.string().optional(),
  dataSource: zod.enum(["upload", "historical", "usps_market_rates"]),

  // Data (rateGrid is NOT saved — rebuilt on restore from dataSource)
  serviceLevelStats: serviceLevelStatsSchema_.nullable(),
  totalShipments: zod.number(),
  oldestShipmentDate: zod.string().nullable(),
  newestShipmentDate: zod.string().nullable(),
  dataError: zod.string().nullable(),
  uploadedFileUrl: zod.string().nullable(),
  destinationZips: zod.array(zod.string()).nullable(),

  // Carrier selection
  merchantAddress: addressSchema_.nullable(),
  dailyVolume: zod.number(),
  carrierEligibility: carrierEligibilitySchema_.nullable(),
  selectedCarriers: zod.array(zod.string()),
  overriddenCarriers: zod.array(zod.string()),

  // Pricing config
  pricingStrategy: zod.enum(PricingStrategy),
  unit: zod.enum(SpreadUnit),
  amount: zod.number(),
  missingMerchantRateMode: zod.enum(MissingMerchantRateMode),
  defaultSpreadNoMerchantRate: zod.number(),
  defaultSpreadUnit: zod.enum(SpreadUnit),
  dhlSettings: dhlSettingsSchema_,
  revShareSettings: revShareSettingsSchema_,

  // Results (quoteResults is NOT saved — recomputed on restore)
});
export const RateCalculatorSessionConfigSchema: zod.ZodType<RateCalculatorSessionConfig> =
  rateCalculatorSessionConfigSchema_;
export interface RateCalculatorSessionConfig
  extends zod.infer<typeof rateCalculatorSessionConfigSchema_> {}
